/**
 * First-login "Set your new password" screen.
 *
 * Accounts created or reset by an administrator with "Require password change at first login"
 * carry `must_change_password: true`. ProtectedRoute sends such users here before any other
 * screen; after a successful change the flag is cleared server-side and the user continues to
 * their dashboard (or the page they originally asked for).
 */
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getHomePath } from '../components/shell/moduleRegistry';
import PasswordField from '../components/shell/PasswordField';
import { Button } from '../components/ui/button';
import { GraduationCap, ShieldCheck, SignOut, Check, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { BRAND_NAME } from '../brand';

const RULES = [
  { key: 'len', label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { key: 'case', label: 'Upper and lower case letters', test: (p) => /[a-z]/.test(p) && /[A-Z]/.test(p) },
  { key: 'num', label: 'A number', test: (p) => /\d/.test(p) },
  { key: 'sym', label: 'A symbol (!@#$…)', test: (p) => /[^A-Za-z0-9]/.test(p) },
];

export default function ChangePasswordPage() {
  const { api, user, setUser, logout, tenantInfo } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [next, setNext] = useState('');
  const [saving, setSaving] = useState(false);

  const forced = !!user?.must_change_password;
  const passed = RULES.filter((r) => r.test(next)).length;
  const strong = passed === RULES.length;
  const canSubmit = next.length >= 6 && !saving;

  const destination = location.state?.from?.pathname || getHomePath(user?.role);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setSaving(true);
    try {
      // The user is already signed in (with the temporary password) — only the new one is needed
      await api.put('/auth/change-password', { new_password: next });
      setUser((u) => (u ? { ...u, must_change_password: false } : u));
      toast.success('Password updated — welcome!');
      navigate(destination, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const brand = tenantInfo?.name && user?.role !== 'super_admin' ? tenantInfo.name : BRAND_NAME;

  return (
    <div className="min-h-screen bg-muted/30 flex flex-col" data-testid="change-password-page">
      <header className="h-14 bg-shell-primary text-white flex items-center px-5 gap-2">
        <GraduationCap className="w-6 h-6" weight="fill" />
        <span className="font-heading font-bold">{brand}</span>
        <span className="ml-auto text-xs text-white/80">{user?.email}</span>
        <Button variant="ghost" size="sm" className="h-8 text-white hover:bg-white/15 hover:text-white" onClick={() => { logout(); navigate('/login'); }} data-testid="change-password-signout">
          <SignOut className="w-4 h-4" /> Sign out
        </Button>
      </header>

      <main className="flex-1 flex items-start justify-center p-4 sm:p-8">
        <div className="w-full max-w-md bg-white border border-border rounded-xl shadow-sm">
          <div className="p-6 border-b border-border flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-5 h-5" weight="fill" />
            </div>
            <div>
              <h1 className="font-heading text-lg font-bold leading-tight">{forced ? 'Set your new password' : 'Change password'}</h1>
              <p className="text-sm text-muted-foreground mt-1">
                {forced
                  ? `Hi ${user?.first_name}, your account was set up with a temporary password. Choose your own password to continue.`
                  : 'Choose a new password for your account.'}
              </p>
            </div>
          </div>

          <form onSubmit={submit} className="p-6 space-y-4" data-testid="change-password-form">
            <PasswordField
              label="New password"
              value={next}
              onChange={setNext}
              required
              generate
              placeholder="At least 6 characters"
              hint="Show it with the eye icon and copy it somewhere safe before you continue."
              testid="new-password-input"
            />
            <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs" data-testid="password-rules">
              {RULES.map((r) => {
                const ok = r.test(next);
                return (
                  <li key={r.key} className={`flex items-center gap-1.5 ${ok ? 'text-emerald-700' : 'text-muted-foreground'}`}>
                    {ok ? <Check className="w-3.5 h-3.5" weight="bold" /> : <X className="w-3.5 h-3.5" />}
                    {r.label}
                  </li>
                );
              })}
            </ul>
            <div className="h-1.5 rounded bg-muted overflow-hidden">
              <div
                className={`h-full transition-all ${strong ? 'bg-emerald-500' : passed >= 2 ? 'bg-amber-500' : 'bg-red-400'}`}
                style={{ width: `${(passed / RULES.length) * 100}%` }}
              />
            </div>
            <Button type="submit" className="w-full h-10" disabled={!canSubmit} data-testid="change-password-submit">
              {saving ? 'Saving…' : forced ? 'Save and continue' : 'Change password'}
            </Button>
            {forced && (
              <p className="text-[11px] text-muted-foreground text-center">
                You can change your password again later under <span className="font-medium">My profile</span>.
              </p>
            )}
          </form>
        </div>
      </main>
    </div>
  );
}
